import { Button, MenuItem, Position } from "@blueprintjs/core";
import React, { useLayoutEffect, useRef } from "react";
import useDimensions from "react-use-dimensions";
import { MultiSelect } from "@blueprintjs/select";
import "styled-components/macro";
import { useAppMode } from "../../store/model-misc";

const ENTER_RETURN_KEY_CODE = 13;

const XCA_REASONS = [
  { codeName: "codeshare", longName: "Code share (duplicate)" },
];

const DEFAULT_REASONS = [
  { codeName: "capacity", longName: "Not enough tickets available" },
  { codeName: "price", longName: "Ticket price higher than current fare" },
  { codeName: "timing", longName: "Flight times won't work" },
  { codeName: "carrier", longName: "The airline isn't suitable" },
  { codeName: "layover", longName: "Has layover and flight must be nonstop" },
  { codeName: "duplicate", longName: "Valid, but using for another trip" },
  { codeName: "group_cancelled", longName: "Group cancelled" },
  { codeName: "group_too_small", longName: "Group too small" },
  { codeName: "already_booked", longName: "Already booked" },
  { codeName: "plane_too_small", longName: "Plane too small" },
  { codeName: "no_air_request", longName: "No air request" },
  { codeName: "wn_being_held", longName: "Southwest being held" },
  {
    codeName: "request_form_incorrect",
    longName: "Air request form has errors",
  },
  {
    codeName: "departure_airport",
    longName: "Departure airport will not work",
  },
  {
    codeName: "arrival_airport",
    longName: "Arrival airport will not work",
  },
  {
    codeName: "test_group",
    longName: "Test group",
  },
  { codeName: "other", longName: "" },
];

export function ReasonsInput({
  queryText,
  setQueryText,
  selectedItems,
  onAdd,
  onClear,
  onRemove,
  activeField,
  setActiveField,
}) {
  const [rmsRef, { width }] = useDimensions();
  const reasonInputRef = useRef(null);

  const handleFocus = () => {
    reasonInputRef.current.focus();
    setActiveField("reasons");
  };
  // const handleBlur = ev => {
  //   ev.preventDefault();
  //   reasonInputRef.current.blur();
  //   setActiveField(null);
  // };

  const clearButton =
    selectedItems.length > 0 ? (
      <Button
        icon="cross"
        minimal={true}
        onClick={onClear}
        data-testid="skipping_form__reasons_clear_all_tags_btn"
      />
    ) : undefined;
  const isItemSelected = (r) => selectedItems.includes(r);
  const onItemSelect = (reason) => {
    if (isItemSelected(reason)) {
      onRemove(reason.codeName);
    } else {
      onAdd(reason);
    }
  };
  const onItemsSelect = (reasons) => reasons.map(onItemSelect);
  useLayoutEffect(() => {
    // noinspection JSUnresolvedFunction
    handleFocus();
    // eslint-disable-next-line
  }, []);

  let reasonItems = [];
  if (useAppMode() === "xca") {
    reasonItems = [...reasonItems, ...XCA_REASONS];
  }
  reasonItems = [...reasonItems, ...DEFAULT_REASONS];

  return (
    <div
      data-testid="skipping_form__reasons_input"
      ref={rmsRef}
      css={`
        .bp3-multi-select-popover .bp3-menu {
          max-width: none !important;
          max-height: 220px !important;
          overflow-y: auto;
        }
      `}
    >
      <MultiSelect
        fill
        createNewItemFromQuery={undefined}
        onKeyDown={(ev) => {
          if (ev.keyCode === ENTER_RETURN_KEY_CODE) {
            ev.preventDefault();
          }
        }}
        resetOnSelect={true}
        createNewItemRenderer={null}
        openOnKeyDown={false}
        items={reasonItems}
        itemsEqual="codeName"
        itemDisabled={isItemSelected}
        itemPredicate={reasonPredicate}
        itemRenderer={reasonItemRenderer(isItemSelected)}
        selectedItems={selectedItems}
        onItemSelect={onItemSelect}
        tagRenderer={reasonTagRenderer}
        onItemsPaste={onItemsSelect}
        placeholder="Choose all that apply..."
        query={queryText}
        onQueryChange={setQueryText}
        popoverProps={{
          usePortal: false,
          minimal: true,
          position: Position.BOTTOM,
        }}
        tagInputProps={{
          onFocus: handleFocus,
          name: "reasons",
          autoFocus: true,
          inputRef: (r) => {
            reasonInputRef.current = r;
          },
          leftIcon: "locate",
          large: true,
          onRemove: onRemove,
          rightElement: clearButton,
          tagProps: { large: false, minimal: true },
        }}
        // TODO: Center for IE11
        css={`
          .bp3-popover-content .bp3-menu {
            width: ${width}px;
          }
        `}
      />
    </div>
  );
}

const reasonPredicate = (query, reason, _index, exactMatch) => {
  const normalizedTitle = reason.codeName.toLowerCase();
  const normalizedQuery = query.toLowerCase();

  if (exactMatch) {
    return normalizedTitle === normalizedQuery;
  } else {
    return (
      `${reason.longName}. ${normalizedTitle}`.indexOf(normalizedQuery) >= 0
    );
  }
};
const reasonTagRenderer = (reason) => reason.codeName;
const reasonItemRenderer = (isItemSelected) => (
  reason,
  { modifiers, handleClick }
) => {
  if (!modifiers.matchesPredicate) {
    return null;
  }
  const isSelected = isItemSelected(reason);
  return (
    <MenuItem
      data-testid={`skipping_form__reasons_input__${reason.codeName}_menuitem`}
      active={modifiers.active}
      icon={isSelected ? "tick" : "blank"}
      disabled={false}
      key={reason.codeName}
      label={reason.longName}
      onClick={handleClick}
      text={reason.codeName}
      shouldDismissPopover={false}
    />
  );
};
